import { Link } from "react-router-dom";
import { ArrowRight, Mail } from "lucide-react";
import { heroYouTubeId, heroVideoStart, heroVideoEnd, heroMp4, school, images } from "../mock/mock";
import CTASection from "../components/CTASection";
import UpcomingCourses from "../components/UpcomingCourses";
import ReviewsPreview from "../components/ReviewsPreview";
import PricesCarousel from "../components/PricesCarousel";
import Steps from "../components/Steps";
import HeroVideo from "../components/HeroVideo";
import useSeo from "../hooks/useSeo";


export default function Home() {
  useSeo({ title: undefined, description: "Trafikskola i Uppsala på Torkelsgatan 29. Startpaket från 6 425 kr, körpaket, intensivpaket, riskettan och halkbana. Teori på sex språk. Boka online dygnet runt." });
  return (
    <main className="pt-20 md:pt-[8.25rem]">
      {/* HERO */}
      <section className="relative flex items-center py-24 md:py-32 min-h-[calc(100svh-5rem)] md:min-h-[calc(100svh-8.25rem)]">
        <div className="absolute inset-0 overflow-hidden bg-[#0B2545]">
          <img
            src={images.uppsala}
            alt="Trafik i Uppsala"
            className="absolute inset-0 w-full h-full object-cover hero-poster"
            data-testid="hero-poster"
          />
          <HeroVideo videoId={heroYouTubeId} start={heroVideoStart} end={heroVideoEnd} mp4={heroMp4} poster={images.uppsala} />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 w-full">
          <div className="max-w-2xl fade-up">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#ffce3a]/20 text-[#ffce3a] font-semibold text-sm mb-6 backdrop-blur-sm border border-[#ffce3a]/30">
              {school.tagline}
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.08] text-shadow-soft">
              Ta ditt körkort hos oss
            </h1>
            <p className="mt-6 text-lg text-slate-200 leading-relaxed max-w-xl">
              Vi erbjuder körkortsutbildning för B-behörighet i en rad olika paket och kurskombinationer – samt riskettan, risktvåan och teori i Uppsala.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <a href={school.bookingUrl} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl bg-[#ffce3a] text-[#0B2545] font-semibold hover:bg-[#ffd85f] transition-colors">
                Boka online
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <a href={`mailto:${school.email}`} className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl bg-white/10 border border-white/25 text-white font-semibold backdrop-blur-sm hover:bg-white/20 transition-colors">
                <Mail size={18} /> Kontakta oss
              </a>
            </div>
          </div>
        </div>
      </section>

      <UpcomingCourses />

      <PricesCarousel />

      <Steps />

      <ReviewsPreview />

      {/* UPPSALA & RECEPTION */}
      <section className="max-w-7xl mx-auto px-5 py-24" data-testid="uppsala-section">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-[#1B6CA8] font-semibold tracking-[0.18em] text-xs uppercase">Mitt i Uppsala</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#0B2545] mt-2">Vi utbildar dig i riktig stadstrafik</h2>
          <p className="text-slate-500 mt-3">Cirkulationsplatser, trafikljus, bussgator och cykelbanor – du lär dig köra där du faktiskt ska köra.</p>
        </div>
        <div className="grid md:grid-cols-5 gap-6">
          <figure className="md:col-span-3 relative rounded-2xl overflow-hidden shadow-xl group">
            <img src={images.uppsala} alt="Trafik i centrala Uppsala" className="w-full h-[380px] object-cover group-hover:scale-[1.03] transition-transform duration-700" data-testid="uppsala-image" />
            <figcaption className="absolute bottom-0 inset-x-0 p-6 bg-gradient-to-t from-[#0B2545]/90 to-transparent text-white">
              <span className="text-[#ffce3a] text-xs font-semibold tracking-[0.18em] uppercase">Uppsala centrum</span>
              <p className="font-display font-bold text-xl mt-1">Här övningskör du med oss</p>
            </figcaption>
          </figure>
          <figure className="md:col-span-2 relative rounded-2xl overflow-hidden shadow-xl group">
            <img src={images.reception} alt="Receptionen hos City Trafikskola Uppsala" className="w-full h-[380px] object-cover group-hover:scale-[1.03] transition-transform duration-700" data-testid="reception-image" />
            <figcaption className="absolute bottom-0 inset-x-0 p-6 bg-gradient-to-t from-[#0B2545]/90 to-transparent text-white">
              <span className="text-[#ffce3a] text-xs font-semibold tracking-[0.18em] uppercase">Vår reception</span>
              <p className="font-display font-bold text-xl mt-1">{school.address}, {school.area}</p>
            </figcaption>
          </figure>
        </div>
      </section>

      <CTASection />
    </main>
  );
}
